import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  IconButton,
  Paper,
  Popper,
  Typography,
  useTheme,
} from '@mui/material';
import {
  deleteColumn,
  deleteRow,
  deleteTable,
  insertTableColumn,
  insertTableRow,
} from '@platejs/table';
import {
  TableCellHeaderPlugin,
  TableCellPlugin,
  TablePlugin,
  TableProvider,
  TableRowPlugin,
} from '@platejs/table/react';
import clsx from 'clsx';
import {
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  Columns3,
  Rows3,
  Trash2,
} from 'lucide-react';
import { NodeApi, PointApi, RangeApi, TextApi } from 'platejs';
import type { PlateElementProps } from 'platejs/react';
import { PlateElement, useEditorPlugin, useEditorRef, useReadOnly } from 'platejs/react';
import { AlineTooltip } from 'src/components';
import { composeRefs } from 'src/helpers/compose-refs';
import { makeStyles } from 'tss-react/mui';

import { focusEditor } from '../utils/focus';
import { getFloatingToolbarStyles, getToolbarButtonStyles } from '../utils/styles';

const useTableStyles = makeStyles()(theme => ({
  floatingToolbar: {
    ...getFloatingToolbarStyles(theme),
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    left: '50%',
    position: 'absolute',
    transform: 'translateX(-50%)',
  },
  floatingToolbarBottom: {
    marginTop: theme.spacing(1),
    top: '100%',
  },
  floatingToolbarButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    borderRadius: 4,
    padding: theme.spacing(0.5),
  },
  floatingToolbarButtonDanger: {
    '&:hover': {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
    },
  },
  floatingToolbarDivider: {
    height: 20,
    margin: theme.spacing(0, 0.25),
  },
  floatingToolbarTop: {
    bottom: '100%',
    marginBottom: theme.spacing(1),
  },
  gridCell: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    height: '18px',
    transition: 'all 0.1s ease',
    width: '18px',
  },
  gridCellHighlighted: {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
  gridContainer: {
    display: 'grid',
    gap: '2px',
    gridTemplateColumns: 'repeat(10, 18px)',
    gridTemplateRows: 'repeat(10, 18px)',
    marginBottom: theme.spacing(1),
  },
  pickerContainer: {
    padding: theme.spacing(1),
  },
  pickerLabel: {
    marginTop: theme.spacing(1),
  },
  table: {
    border: `1px solid ${theme.palette.divider}`,
    borderCollapse: 'collapse',
    borderRadius: theme.shape.borderRadius,
    margin: 0,
    width: '100%',
  },
  tableCell: {
    border: `1px solid ${theme.palette.divider}`,
    minHeight: '1em',
    minWidth: 80,
    padding: theme.spacing(1),
    position: 'relative',
  },
  tableCellHeader: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    fontWeight: 'bold',
    minHeight: '1em',
    minWidth: 80,
    padding: theme.spacing(1),
    position: 'relative',
  },
  tableWrapper: {
    margin: theme.spacing(2, 0),
    maxWidth: '100%',
    overflow: 'visible',
    position: 'relative',
  },
  // Toolbar button styles (shared)
  ...getToolbarButtonStyles(theme),
}));

const TableFloatingToolbar = React.memo(function TableFloatingToolbar({
  onClose,
  tableRef,
}: {
  onClose: () => void;
  tableRef: React.RefObject<HTMLDivElement | null>;
}): JSX.Element {
  const { classes } = useTableStyles();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const editor = useEditorRef();
  const [position, setPosition] = useState<'top' | 'bottom'>('top');

  // Calculate position based on available space within scrollable container
  useEffect(() => {
    if (!tableRef.current) return;

    // Find the scrollable parent container (the textarea/editor area)
    const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
      let parent = element.parentElement;
      while (parent) {
        const { overflow, overflowY } = getComputedStyle(parent);
        if (
          overflow === 'auto' ||
          overflow === 'scroll' ||
          overflowY === 'auto' ||
          overflowY === 'scroll'
        ) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const scrollableParent = findScrollableParent(tableRef.current);

    const updatePosition = () => {
      if (!tableRef.current || !toolbarRef.current) return;

      const tableRect = tableRef.current.getBoundingClientRect();
      const toolbarHeight = toolbarRef.current.offsetHeight;

      if (scrollableParent) {
        // Check space relative to the scrollable container
        const containerRect = scrollableParent.getBoundingClientRect();
        const spaceAboveInContainer = tableRect.top - containerRect.top;

        // If not enough space above within the container, position below
        setPosition(spaceAboveInContainer < toolbarHeight ? 'bottom' : 'top');
      } else {
        // Fallback to viewport-based positioning
        setPosition(tableRect.top < toolbarHeight ? 'bottom' : 'top');
      }
    };

    updatePosition();

    // Update on scroll (capture phase to catch scrolls on any element) and resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [tableRef]);

  const handleInsertRowAbove = useCallback(() => {
    insertTableRow(editor, { before: true });
  }, [editor]);

  const handleInsertRowBelow = useCallback(() => {
    insertTableRow(editor);
  }, [editor]);

  const handleInsertColumnLeft = useCallback(() => {
    insertTableColumn(editor, { before: true });
  }, [editor]);

  const handleInsertColumnRight = useCallback(() => {
    insertTableColumn(editor);
  }, [editor]);

  const handleDeleteRow = useCallback(() => {
    deleteRow(editor);
  }, [editor]);

  const handleDeleteColumn = useCallback(() => {
    deleteColumn(editor);
  }, [editor]);

  const handleDeleteTable = useCallback(() => {
    deleteTable(editor);
    onClose();
  }, [editor, onClose]);

  return (
    <Paper
      ref={toolbarRef}
      className={clsx(
        classes.floatingToolbar,
        position === 'top' ? classes.floatingToolbarTop : classes.floatingToolbarBottom
      )}
      contentEditable={false}
      elevation={0}
    >
      <AlineTooltip arrow placement="top" title="Insert row above">
        <IconButton
          className={classes.floatingToolbarButton}
          size="small"
          onClick={handleInsertRowAbove}
        >
          <ArrowUpToLine size={16} />
        </IconButton>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title="Insert row below">
        <IconButton
          className={classes.floatingToolbarButton}
          size="small"
          onClick={handleInsertRowBelow}
        >
          <ArrowDownToLine size={16} />
        </IconButton>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title="Insert column left">
        <IconButton
          className={classes.floatingToolbarButton}
          size="small"
          onClick={handleInsertColumnLeft}
        >
          <ArrowLeftToLine size={16} />
        </IconButton>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title="Insert column right">
        <IconButton
          className={classes.floatingToolbarButton}
          size="small"
          onClick={handleInsertColumnRight}
        >
          <ArrowRightToLine size={16} />
        </IconButton>
      </AlineTooltip>

      <Divider className={classes.floatingToolbarDivider} orientation="vertical" />

      <AlineTooltip arrow placement="top" title="Delete row">
        <IconButton
          className={classes.floatingToolbarButton}
          size="small"
          onClick={handleDeleteRow}
        >
          <Rows3 size={16} />
        </IconButton>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title="Delete column">
        <IconButton
          className={classes.floatingToolbarButton}
          size="small"
          onClick={handleDeleteColumn}
        >
          <Columns3 size={16} />
        </IconButton>
      </AlineTooltip>

      <Divider className={classes.floatingToolbarDivider} orientation="vertical" />

      <AlineTooltip arrow placement="top" title="Delete table">
        <IconButton
          className={clsx(classes.floatingToolbarButton, classes.floatingToolbarButtonDanger)}
          size="small"
          onClick={handleDeleteTable}
        >
          <Trash2 size={16} />
        </IconButton>
      </AlineTooltip>
    </Paper>
  );
});

/** Table element component with floating toolbar */
export const TableElement = React.memo(function TableElement(
  props: PlateElementProps
): JSX.Element {
  const editor = useEditorRef();
  const readOnly = useReadOnly();
  const { classes } = useTableStyles();
  const tableRef = useRef<HTMLDivElement>(null);
  const [showToolbar, setShowToolbar] = useState(false);

  const handleTableClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't show toolbar in read-only mode (e.g., chat history)
      if (readOnly) return;
      e.stopPropagation();
      setShowToolbar(true);
    },
    [readOnly]
  );

  const handleCloseToolbar = useCallback(() => {
    setShowToolbar(false);
    focusEditor(editor);
  }, [editor]);

  // Close toolbar when clicking outside the table
  useEffect(() => {
    if (!showToolbar) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        tableRef.current &&
        event.target instanceof Node &&
        !tableRef.current.contains(event.target)
      )
        handleCloseToolbar();
    };

    // Use setTimeout to avoid closing immediately on the same click that opened it.
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showToolbar, handleCloseToolbar]);

  return (
    <div ref={tableRef} className={classes.tableWrapper} onMouseDown={handleTableClick}>
      {showToolbar && <TableFloatingToolbar tableRef={tableRef} onClose={handleCloseToolbar} />}
      <TableProvider>
        <PlateElement {...props} as="table" className={classes.table}>
          <tbody>{props.children}</tbody>
        </PlateElement>
      </TableProvider>
    </div>
  );
});

const TableRowElement = React.memo(function TableRowElement(props: PlateElementProps): JSX.Element {
  return (
    <PlateElement {...props} as="tr">
      {props.children}
    </PlateElement>
  );
});

const TableCellElement = React.memo(function TableCellElement(
  props: PlateElementProps
): JSX.Element {
  const { classes } = useTableStyles();

  return (
    <PlateElement {...props} as="td" className={classes.tableCell}>
      {props.children}
    </PlateElement>
  );
});

const TableCellHeaderElement = React.memo(function TableCellHeaderElement(
  props: PlateElementProps
): JSX.Element {
  const { classes } = useTableStyles();

  return (
    <PlateElement {...props} as="th" className={classes.tableCellHeader}>
      {props.children}
    </PlateElement>
  );
});

interface TableSizePickerProps {
  onSelectSize: (rows: number, cols: number) => void;
}

/** Grid-based table size picker */
export const TableSizePicker = React.forwardRef<HTMLDivElement, TableSizePickerProps>(
  function TableSizePicker({ onSelectSize }, ref): JSX.Element {
    const GRID_ROWS = 10;
    const GRID_COLS = 10;
    const { classes } = useTableStyles();
    const [hoveredCell, setHoveredCell] = useState<{ col: number; row: number } | null>(null);

    const handleCellClick = (row: number, col: number) => {
      // Convert from 0-based to 1-based (user sees 1x1, not 0x0)
      onSelectSize(row + 1, col + 1);
    };

    const isCellHighlighted = (row: number, col: number): boolean => {
      if (!hoveredCell) return false;
      return row <= hoveredCell.row && col <= hoveredCell.col;
    };

    const getTableSizeLabel = (): string => {
      if (!hoveredCell) return '0 x 0';
      return `${hoveredCell.row + 1} x ${hoveredCell.col + 1} Table`;
    };

    return (
      <Box ref={ref} className={classes.pickerContainer}>
        <Box className={classes.gridContainer}>
          {Array.from({ length: GRID_ROWS }).map((_, rowIndex) =>
            Array.from({ length: GRID_COLS }).map((_, colIndex) => (
              <Box
                key={`cell-${rowIndex}-${colIndex}`}
                className={clsx(
                  classes.gridCell,
                  isCellHighlighted(rowIndex, colIndex) && classes.gridCellHighlighted
                )}
                onClick={() => handleCellClick(rowIndex, colIndex)}
                onMouseEnter={() => setHoveredCell({ col: colIndex, row: rowIndex })}
              />
            ))
          )}
        </Box>

        <Typography align="center" className={classes.pickerLabel} variant="body2">
          {getTableSizeLabel()}
        </Typography>
      </Box>
    );
  }
);

/** Table toolbar button with size picker popper */
export const TableToolbarButton = React.forwardRef<
  HTMLElement,
  {
    children: React.ReactNode;
    tooltip: string;
    afterClose?: () => void;
    disabled?: boolean;
  }
>(function TableToolbarButton({ afterClose, children, disabled, tooltip }, ref): JSX.Element {
  const theme = useTheme();
  const editor = useEditorRef();
  const { classes } = useTableStyles();
  const { tf } = useEditorPlugin(TablePlugin);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const handleButtonClick = () => {
    setOpen(prev => !prev);
  };

  const handleClose = () => {
    setOpen(false);
    afterClose?.();
    focusEditor(editor);
  };

  const handleInsertTable = (rows: number, cols: number) => {
    /**
     * Insert table with selected dimensions (no header row).
     * select: true auto-selects the first cell after insertion.
     */
    tf.insert.table({ colCount: cols, header: false, rowCount: rows }, { select: true });
    handleClose();
  };

  return (
    <>
      <AlineTooltip arrow placement="top" title={disabled ? '' : tooltip}>
        <IconButton
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ref={composeRefs(ref, buttonRef) as any}
          className={clsx(classes.toolbarButton, classes.toolbarButtonDefault)}
          disabled={disabled}
          size="small"
          style={open ? { backgroundColor: theme.palette.action.hover } : undefined}
          onClick={handleButtonClick}
        >
          {children}
        </IconButton>
      </AlineTooltip>
      <Popper
        transition
        anchorEl={buttonRef.current}
        className="ignore-click-outside/toolbar"
        modifiers={[
          { enabled: true, name: 'flip' },
          { enabled: true, name: 'preventOverflow', options: { padding: 8 } },
          { name: 'offset', options: { offset: [0, 8] } },
        ]}
        open={open}
        placement="bottom-end"
        sx={{ zIndex: theme.zIndex.modal }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={8}>
              <ClickAwayListener onClickAway={handleClose}>
                <TableSizePicker onSelectSize={handleInsertTable} />
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
});

/** Configured TablePlugin kit with all table-related plugins */
export const TableKit = [
  TablePlugin.configure({
    options: {
      minColumnWidth: 80,
    },
    render: {
      node: TableElement,
    },
  }).overrideEditor(({ editor, tf: { deleteBackward, deleteFragment, selectAll }, type }) => ({
    transforms: {
      deleteBackward(unit) {
        const { selection } = editor;
        if (selection && RangeApi.isCollapsed(selection)) {
          const { anchor } = selection;
          const blockPath = [anchor.path[0]];
          /**
           * The start of the block, not merely offset 0: just after an inline chip the caret
           * is at offset 0 of a later text node, and Backspace there removes the chip.
           */
          if (
            anchor.path.length === 2 &&
            anchor.path[0] > 0 &&
            editor.api.isStart(anchor, blockPath)
          ) {
            const previousPath = [anchor.path[0] - 1];
            const previous = editor.api.node(previousPath);
            if (
              previous?.[0].type === type &&
              Array.from(NodeApi.descendants(previous[0])).every(([node]) =>
                TextApi.isText(node)
                  ? node.text === ''
                  : ['tr', 'td', 'th', 'p'].includes(node.type)
              )
            ) {
              editor.tf.removeNodes({ at: previousPath });
              return;
            }
          }
        }
        deleteBackward(unit);
      },
      deleteFragment(direction) {
        const { selection } = editor;
        if (selection && RangeApi.isExpanded(selection)) {
          const [start, end] = RangeApi.edges(selection);
          const table = editor.api.above({ at: start, match: { type } });
          const tableStart = table && editor.api.start(table[1]);
          const tableEnd = table && editor.api.end(table[1]);
          if (
            table &&
            tableStart &&
            tableEnd &&
            PointApi.equals(start, tableStart) &&
            PointApi.equals(end, tableEnd)
          ) {
            editor.tf.withoutNormalizing(() => {
              editor.tf.removeNodes({ at: table[1] });
              if (editor.children.length === 0)
                editor.tf.insertNodes(editor.api.create.block(), { at: [0] });
              if (!editor.selection)
                editor.tf.select(
                  editor.api.start([Math.min(table[1][0], editor.children.length - 1)])
                );
            });
            return;
          }
        }
        deleteFragment(direction);
      },
      selectAll() {
        if (editor.api.above({ match: { type } })) return selectAll();
        editor.tf.select([]);
        return true;
      },
    },
  })),
  TableRowPlugin.configure({
    render: {
      node: TableRowElement,
    },
  }),
  TableCellPlugin.configure({
    render: {
      node: TableCellElement,
    },
  }),
  TableCellHeaderPlugin.configure({
    render: {
      node: TableCellHeaderElement,
    },
  }),
];
